/**
 * Casino and table profiles.
 *
 * Payouts, limits and the zero rule are data, not constants (CLAUDE.md
 * invariant 6). The standard figures in `bets.ts` are the *default profile* and
 * a reference to compare against — never the source of a displayed number.
 *
 * Two units, both integer:
 *   chips  — abstract base units, live on the spread, portable between venues
 *   cents  — money, lives here on the table profile
 *   money  = chips × baseUnit
 */

import type {
  BetLimit,
  BetType,
  CasinoProfile,
  TableLimits,
  TableProfile,
  WheelStyle,
} from './types';
import { PAYOUTS, isEvenMoneyBet } from './bets';
import { WHEEL_STYLES } from './wheels';

// The shapes live in types.ts per CLAUDE.md; re-exported here so callers can
// take the profile types and the profile functions from one place.
export type { BetLimit, CasinoProfile, TableLimits, TableProfile, ZeroRule } from './types';

// ---------------------------------------------------------------------------
// Bet classification
// ---------------------------------------------------------------------------

const INSIDE_BETS: ReadonlySet<BetType> = new Set<BetType>([
  'straight', 'split', 'street', 'corner', 'six-line', 'basket',
]);

export function isInsideBet(betType: BetType): boolean {
  return INSIDE_BETS.has(betType);
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/** Payout for a bet at this table. Falls back to the standard odds. */
export function payoutFor(profile: TableProfile, betType: BetType): number {
  const override = profile.payouts[betType];
  return override ?? PAYOUTS[betType];
}

/** Stake bounds for a bet at this table, in cents. */
export function limitFor(profile: TableProfile, betType: BetType): BetLimit {
  const override = profile.limits.overrides?.[betType];
  if (override) return override;
  return isInsideBet(betType) ? profile.limits.inside : profile.limits.outside;
}

/** Money value of a chip stake at this table, in integer cents. */
export function stakeCents(profile: TableProfile, chips: number): number {
  return chips * profile.baseUnit;
}

export type LimitCheck =
  | { ok: true }
  | { ok: false; reason: 'below-minimum' | 'above-maximum'; limit: BetLimit; stake: number };

export function checkLimit(profile: TableProfile, betType: BetType, chips: number): LimitCheck {
  const limit = limitFor(profile, betType);
  const stake = stakeCents(profile, chips);
  if (stake < limit.min) return { ok: false, reason: 'below-minimum', limit, stake };
  if (stake > limit.max) return { ok: false, reason: 'above-maximum', limit, stake };
  return { ok: true };
}

/**
 * Largest whole-chip stake that still fits under a bet's maximum — what the
 * table editor means by "N chips max", and the ceiling a stacked strategy runs
 * into once a chip value turns its layers into money (SPEC-V2.md §4.4).
 */
export function maxChipsFor(profile: TableProfile, betType: BetType): number {
  return Math.floor(limitFor(profile, betType).max / profile.baseUnit);
}

// ---------------------------------------------------------------------------
// Zero rules
// ---------------------------------------------------------------------------

/**
 * What comes back on a losing even-money bet when a zero hits, in cents.
 *
 * `la-partage` and `surrender` return half the stake immediately.
 *
 * `en-prison` is not a single-spin rule — the stake is held for another spin.
 * Under the standard rule, where a stake meeting a second zero *stays*
 * imprisoned, the recursion solves to returning half the stake in expectation,
 * so its per-spin mean is identical to la partage. That is exact for expected
 * value and wrong for everything else: the outcome distribution differs, which
 * is why the simulator (§8) must carry the imprisoned stake properly rather
 * than reuse this function.
 *
 * Refunds floor — rounding against the player, never for them.
 */
export function zeroRefundCents(
  profile: TableProfile,
  betType: BetType,
  stake: number,
): number {
  if (!isEvenMoneyBet(betType)) return 0;
  switch (profile.zeroRule) {
    case 'la-partage':
    case 'surrender':
    case 'en-prison':
      return Math.floor(stake / 2);
    case 'none':
      return 0;
  }
}

/** True when the rule changes even-money outcomes at all. */
export function hasZeroRule(profile: TableProfile): boolean {
  return profile.zeroRule !== 'none';
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export const DEFAULT_CASINO_ID = 'casino_default';
export const DEFAULT_TABLE_IDS: Readonly<Record<WheelStyle, string>> = {
  'single-zero': 'table_default_single',
  'double-zero': 'table_default_double',
};

/** Wide enough not to get in the way before anything is configured. */
const DEFAULT_LIMITS: TableLimits = {
  inside: { min: 100, max: 50_000 },
  outside: { min: 500, max: 500_000 },
};

export const DEFAULT_CASINO: CasinoProfile = {
  id: DEFAULT_CASINO_ID,
  name: 'Standard rules',
  currency: 'AUD',
  denominations: [100, 250, 500, 2500, 10_000],
  createdAt: 0,
  updatedAt: 0,
};

function defaultTable(style: WheelStyle, name: string): TableProfile {
  return {
    id: DEFAULT_TABLE_IDS[style],
    casinoId: DEFAULT_CASINO_ID,
    name,
    wheelStyle: style,
    baseUnit: 100,
    payouts: {},
    limits: DEFAULT_LIMITS,
    zeroRule: 'none',
    createdAt: 0,
    updatedAt: 0,
  };
}

export const DEFAULT_TABLES: Readonly<Record<WheelStyle, TableProfile>> = {
  'single-zero': defaultTable('single-zero', 'Single zero — standard'),
  'double-zero': defaultTable('double-zero', 'Double zero — standard'),
};

/** The profile every analysis falls back to when none is chosen. */
export function defaultTableFor(style: WheelStyle): TableProfile {
  return DEFAULT_TABLES[style];
}

export function isDefaultTable(profile: TableProfile): boolean {
  return profile.id === DEFAULT_TABLE_IDS[profile.wheelStyle];
}

/**
 * The built-in casino and its two tables are reference data, not user data:
 * they are the standard figures every configured table is measured against
 * (invariant 6), so nothing edits them and the reference cannot drift. A venue
 * of your own is a casino of your own.
 */
export function isBuiltInCasino(casinoId: string): boolean {
  return casinoId === DEFAULT_CASINO_ID;
}

/**
 * Display order for a casino's tables: by wheel style as `wheels.ts` lists
 * them, then by name. Storage hands them back in id order, which records only
 * when each table happened to be created.
 */
export function sortTables(tables: readonly TableProfile[]): TableProfile[] {
  return [...tables].sort(
    (a, b) =>
      WHEEL_STYLES.indexOf(a.wheelStyle) - WHEEL_STYLES.indexOf(b.wheelStyle) ||
      a.name.localeCompare(b.name),
  );
}

// ---------------------------------------------------------------------------
// Construction and validation
// ---------------------------------------------------------------------------

function newId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.floor(Math.random() * 0xffffff).toString(36)}`;
}

export function createCasino(name: string, overrides: Partial<CasinoProfile> = {}): CasinoProfile {
  const now = Date.now();
  return {
    id: newId('casino'),
    name,
    currency: DEFAULT_CASINO.currency,
    denominations: [...DEFAULT_CASINO.denominations],
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
}

export function createTable(
  casinoId: string,
  name: string,
  style: WheelStyle,
  overrides: Partial<TableProfile> = {},
): TableProfile {
  const now = Date.now();
  return {
    id: newId('table'),
    casinoId,
    name,
    wheelStyle: style,
    baseUnit: 100,
    payouts: {},
    limits: DEFAULT_LIMITS,
    zeroRule: 'none',
    createdAt: now,
    updatedAt: now,
    ...overrides,
  };
}

/** Duplicating a table is how most real setups get made. */
export function duplicateTable(profile: TableProfile, name: string): TableProfile {
  const now = Date.now();
  return {
    ...profile,
    id: newId('table'),
    name,
    payouts: { ...profile.payouts },
    limits: {
      inside: { ...profile.limits.inside },
      outside: { ...profile.limits.outside },
      ...(profile.limits.overrides ? { overrides: { ...profile.limits.overrides } } : {}),
    },
    createdAt: now,
    updatedAt: now,
  };
}

/** Problems that make a table unusable. Empty means valid. */
export function validateTable(profile: TableProfile): string[] {
  const errors: string[] = [];

  if (!profile.name.trim()) errors.push('Table needs a name');
  if (!Number.isInteger(profile.baseUnit) || profile.baseUnit <= 0) {
    errors.push('Base unit must be a positive whole number of cents');
  }

  for (const [betType, payout] of Object.entries(profile.payouts)) {
    if (payout === undefined) continue;
    if (!Number.isFinite(payout) || payout < 0) {
      errors.push(`Payout for ${betType} must be zero or more`);
    }
  }

  const checkLimits = (label: string, limit: BetLimit) => {
    if (!Number.isInteger(limit.min) || limit.min < 0) errors.push(`${label} minimum must be a whole number of cents`);
    if (!Number.isInteger(limit.max) || limit.max <= 0) errors.push(`${label} maximum must be a positive whole number of cents`);
    if (limit.min > limit.max) errors.push(`${label} minimum cannot exceed its maximum`);
    if (limit.max < profile.baseUnit) {
      errors.push(`${label} maximum is below one chip, so no bet could ever be placed`);
    }
  };
  checkLimits('Inside', profile.limits.inside);
  checkLimits('Outside', profile.limits.outside);
  for (const [betType, limit] of Object.entries(profile.limits.overrides ?? {})) {
    if (limit) checkLimits(`${betType} override`, limit);
  }

  return errors;
}

/**
 * Things worth saying about a table that are not errors. Surrender does exist
 * on some double-zero tables, so this warns rather than blocks.
 */
export function tableWarnings(profile: TableProfile): string[] {
  const warnings: string[] = [];
  if (profile.zeroRule !== 'none' && profile.wheelStyle === 'double-zero') {
    warnings.push('Zero rules are unusual on a double-zero table — check the table sign');
  }
  if (profile.zeroRule === 'en-prison') {
    warnings.push(
      'En prison is resolved exactly in simulation; the static edge assumes a stake meeting a second zero stays imprisoned',
    );
  }
  for (const deviation of payoutDeviations(profile)) {
    warnings.push(
      `${deviation.betType} pays ${deviation.actual}:1 here, against a standard ${deviation.standard}:1`,
    );
  }
  return warnings;
}

export function validateCasino(profile: CasinoProfile): string[] {
  const errors: string[] = [];
  if (!profile.name.trim()) errors.push('Casino needs a name');
  if (profile.denominations.length === 0) errors.push('At least one chip denomination is needed');
  for (const value of profile.denominations) {
    if (!Number.isInteger(value) || value <= 0) {
      errors.push('Chip denominations must be positive whole numbers of cents');
      break;
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Deviation from standard
// ---------------------------------------------------------------------------

export interface PayoutDeviation {
  readonly betType: BetType;
  readonly standard: number;
  readonly actual: number;
}

/**
 * Where this table departs from the standard odds. §9 requires deviations be
 * impossible to miss, so the UI needs them enumerated rather than inferred.
 */
export function payoutDeviations(profile: TableProfile): PayoutDeviation[] {
  const out: PayoutDeviation[] = [];
  for (const [betType, actual] of Object.entries(profile.payouts) as [BetType, number][]) {
    const standard = PAYOUTS[betType];
    if (actual !== standard) out.push({ betType, standard, actual });
  }
  return out;
}

export function deviatesFromStandard(profile: TableProfile): boolean {
  return payoutDeviations(profile).length > 0 || profile.zeroRule !== 'none';
}

/** Stable content key, so memoised analyses invalidate when a table is edited. */
export function tableSignature(profile: TableProfile): string {
  const payouts = Object.entries(profile.payouts)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([betType, value]) => `${betType}=${value}`)
    .join(',');
  const overrides = Object.entries(profile.limits.overrides ?? {})
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([betType, limit]) => `${betType}=${limit?.min}-${limit?.max}`)
    .join(',');
  return [
    profile.id,
    profile.wheelStyle,
    profile.baseUnit,
    profile.zeroRule,
    payouts,
    `${profile.limits.inside.min}-${profile.limits.inside.max}`,
    `${profile.limits.outside.min}-${profile.limits.outside.max}`,
    overrides,
  ].join('|');
}

// ---------------------------------------------------------------------------
// Money formatting
// ---------------------------------------------------------------------------

/**
 * Parse a typed money value to integer cents. Returns `null` when it is not a
 * money value at all.
 *
 * Deliberately string-based rather than `parseFloat(x) * 100`, which turns
 * "2.50" into 250.00000000000003 on the way past. Nothing here ever touches a
 * float.
 */
export function parseCents(input: string): number | null {
  const cleaned = input.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = (match[2] ?? '').padEnd(2, '0');
  return whole * 100 + Number(fraction);
}

/** Integer cents as a plain editable number, for form fields. */
export function centsToInput(cents: number): string {
  const whole = Math.floor(cents / 100);
  const part = cents % 100;
  return part === 0 ? String(whole) : `${whole}.${String(part).padStart(2, '0')}`;
}

/** Integer cents to a display string. Never used for arithmetic. */
export function formatCents(cents: number, currency = 'AUD'): string {
  const sign = cents < 0 ? '−' : '';
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100);
  const part = abs % 100;
  const symbol = currency === 'AUD' || currency === 'USD' ? '$' : '';
  const body = part === 0 ? `${whole}` : `${whole}.${String(part).padStart(2, '0')}`;
  return `${sign}${symbol}${body}`;
}
